/**
 * Interfaces section (ADR-0025 / ADR-0028) — the app's typed *faces* over one
 * core.
 *
 * The "web" face (pages/portal) is implicit, so this section manages the NEW
 * faces — an assistant that answers/acts over the same tables + logic + RLS,
 * in two surfaces (ADR-0049, independent brains): a **voice** assistant
 * (Jarvis-style) and a **chat** assistant (typed). One of each kind per app.
 *
 * The assistant's DATA scope is NOT set here — it rides on the app **role**
 * (`app_roles.assistant_scope`, ADR-0028 N1), so this editor only carries the
 * persona (+ the voice-only wake word) + which agent/team/model drives it.
 *
 * UX (owner, 2026-07-03): an assistant interface is NOT "created" — the tab
 * always shows ALL its settings with an **Abilitato** toggle at the top that
 * decides whether the interface is present. So in the builder (`focusKind`
 * set) we render an INLINE panel, not a list + create dialog. The legacy
 * list + dialog survives only for the full (`!focusKind`) view.
 *
 * This renders INSIDE the builder, so the "no native `<select>`" standing rule
 * applies: the agent/model pickers are `ui/combobox`. A native one here doesn't
 * just look off-brand — the global `select { h-8 }` base is a FIXED height, so
 * any local `py-*` eats the content box from the inside and CLIPS the text.
 * i18n: English fallbacks in code, Italian in it.json (repo convention).
 */
import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { MessageSquare, Mic, Plus, Sparkles, Trash2, X } from "lucide-react";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SectionHeader } from "@/components/apps/SectionHeader";
import { RestInterfaceEditor } from "@/components/apps/RestInterfaceEditor";
import { appsApi } from "@/lib/api-apps";
import type { InterfaceOut } from "@/lib/api-apps";
import { agentsApi, llmConfigsApi, teamsApi } from "@/lib/api-ai";

/** The assistant surfaces this section manages (ADR-0028 Block E / ADR-0049). */
type AssistantKind = "voice" | "chat";

interface AssistantConfig {
  persona?: string;
  /** Run the in-browser "hey jarvis" wake word for hands-free open (voice only). */
  wake_word?: boolean;
  /** Operator-authored opening questions for the empty chat (Wave-1). Drafted via AI,
   *  edited here; they WIN over the role-grounded fallback on the portal read path. */
  starters?: string[];
}

const KIND_ICON = { voice: Mic, chat: MessageSquare } as const;

export function InterfacesSection({
  appId,
  canWrite,
  // ADR-0049 Slice B — when set, the section is the editor for ONE assistant
  // interface (Chat ≠ Voce, two flat front doors); the other kind is hidden.
  focusKind,
}: {
  appId: string;
  canWrite: boolean;
  focusKind?: AssistantKind;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [edit, setEdit] = React.useState<{
    open: boolean;
    iface: InterfaceOut | null;
    kind: AssistantKind;
  }>({ open: false, iface: null, kind: focusKind ?? "voice" });

  const ifacesQ = useQuery({
    queryKey: ["apps", appId, "interfaces"],
    queryFn: () => appsApi.listInterfaces(appId),
  });
  const allInterfaces = ifacesQ.data ?? [];

  // Hoisted above the focusKind early-return so hook order stays stable; only the
  // legacy (!focusKind) list uses it.
  const del = useMutation({
    mutationFn: (id: string) => appsApi.deleteInterface(appId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["apps", appId, "interfaces"] }),
  });

  // ── Builder path (focusKind): the tab IS the single-interface editor. No
  // create/list — all settings inline + an Abilitato toggle (owner UX). ────────
  if (focusKind) {
    const iface = allInterfaces.find((i) => i.kind === focusKind) ?? null;
    return (
      <AssistantInterfacePanel
        // Remount with fresh state once the row appears (create) or its id changes —
        // and, crucially, when the KIND changes: the panel seeds every field from
        // `iface`/`kind` via `useState`, which only runs at mount. Without `kind` in
        // the key, switching Chat↔Voce while the target row doesn't exist yet leaves
        // BOTH on key "new" → no remount → the chat form's state (agent, name, …)
        // silently becomes the voice interface's.
        key={`${focusKind}:${iface?.id ?? "new"}`}
        appId={appId}
        kind={focusKind}
        iface={iface}
        canWrite={canWrite}
        loading={ifacesQ.isLoading}
        onSaved={() => qc.invalidateQueries({ queryKey: ["apps", appId, "interfaces"] })}
      />
    );
  }

  // ── Full view (no focusKind): legacy list + REST peer. Kept for the aggregate
  // interfaces view; the builder never hits this branch. ──────────────────────
  // REST is a face too (ADR-0051) but has its own editor, so it's excluded here.
  const interfaces = allInterfaces.filter((i) => i.kind !== "rest");
  const showVoiceAdd = !interfaces.some((i) => i.kind === "voice");
  const showChatAdd = !interfaces.some((i) => i.kind === "chat");

  const kindLabel = (kind: string) =>
    kind === "voice"
      ? t("apps.interfaceVoice", "Voice assistant")
      : kind === "chat"
        ? t("apps.interfaceChat", "Chat assistant")
        : kind;

  return (
    <div className="space-y-4">
      <SectionHeader
        icon={Mic}
        title={t("apps.interfacesTitle", "Interfaces")}
        count={interfaces.length}
        action={
          canWrite && (
            <div className="flex items-center gap-2">
              {showVoiceAdd && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEdit({ open: true, iface: null, kind: "voice" })}
                >
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                  {t("apps.interfaceAddVoice", "Add voice assistant")}
                </Button>
              )}
              {showChatAdd && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEdit({ open: true, iface: null, kind: "chat" })}
                >
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                  {t("apps.interfaceAddChat", "Add chat assistant")}
                </Button>
              )}
            </div>
          )
        }
      />
      <p className="text-xs text-muted-foreground">
        {t(
          "apps.interfacesHelp",
          "A face over this app's data. The web portal is always on; add a voice or chat assistant that answers over the same tables (respecting each user's permissions). What it may read is set per role.",
        )}
      </p>

      {interfaces.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
          {t("apps.interfacesEmpty", "No extra interfaces yet — add a voice or chat assistant.")}
        </div>
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border">
          {interfaces.map((i) => {
            const Icon = KIND_ICON[i.kind as AssistantKind] ?? Mic;
            return (
              <div key={i.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <Icon className="h-4 w-4 shrink-0 text-accent" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">{i.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {kindLabel(i.kind)}
                      {!i.enabled && ` · ${t("apps.interfaceDisabled", "disabled")}`}
                    </div>
                  </div>
                </div>
                {canWrite && (
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setEdit({ open: true, iface: i, kind: (i.kind as AssistantKind) ?? "voice" })
                      }
                    >
                      {t("common.edit", "Edit")}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t("common.delete", "Delete")}
                      disabled={del.isPending}
                      onClick={() => {
                        if (window.confirm(t("apps.interfaceDeleteConfirm", "Delete this interface?")))
                          del.mutate(i.id);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* REST is a peer face (ADR-0051) — its own endpoints editor. */}
      <div className="pt-2">
        <RestInterfaceEditor appId={appId} canWrite={canWrite} />
      </div>

      {edit.open && (
        <AssistantInterfaceDialog
          appId={appId}
          kind={edit.kind}
          iface={edit.iface}
          onClose={() => setEdit({ open: false, iface: null, kind: edit.kind })}
        />
      )}
    </div>
  );
}

/**
 * The shared assistant-interface form logic + fields, used by BOTH the inline
 * builder panel and the legacy dialog. Holds all editable state, the pickers
 * (agent / model / team), and the upsert mutation. `enabled` is kept OUT of
 * `fields` because the two layouts place it differently (a prominent top toggle
 * in the panel vs a row in the dialog).
 */
function useAssistantInterfaceForm({
  appId,
  kind,
  iface,
  onSaved,
  // The inline panel uses the Abilitato toggle to govern presence: a brand-new
  // interface is only created once enabled. The dialog always creates on Save.
  gateCreateOnEnabled = false,
}: {
  appId: string;
  kind: AssistantKind;
  iface: InterfaceOut | null;
  onSaved?: () => void;
  gateCreateOnEnabled?: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const cfg = (iface?.config ?? {}) as AssistantConfig;
  const isVoice = (iface?.kind ?? kind) === "voice";
  const [name, setName] = React.useState(iface?.name ?? (isVoice ? "Assistant" : "Chat"));
  const [persona, setPersona] = React.useState(cfg.persona ?? "");
  // In the gated (panel) case a not-yet-created interface starts DISABLED —
  // enabling it IS what creates it. The dialog defaults to on (creates on Save).
  const [enabled, setEnabled] = React.useState(iface?.enabled ?? !gateCreateOnEnabled);
  // ADR-0049 exposure — two ORTHOGONAL capabilities (default = today's embedded widget).
  const [standalone, setStandalone] = React.useState(iface?.standalone ?? false);
  const [embedded, setEmbedded] = React.useState(iface?.embedded ?? true);
  const [wakeWord, setWakeWord] = React.useState(cfg.wake_word ?? false);
  // Operator-authored empty-chat suggestions (Wave-1). Edited here, drafted via AI.
  const [starters, setStarters] = React.useState<string[]>(cfg.starters ?? []);
  const [proposing, setProposing] = React.useState(false);
  const [proposeErr, setProposeErr] = React.useState<string | null>(null);
  // Per-app LLM config override (ADR-0033 Phase B); "" ⇒ inherit the global agent.
  const [llmConfigId, setLlmConfigId] = React.useState(iface?.llm_config_id ?? "");
  // The assistant driving this interface: "agent:<id>" ⇒ a custom agent (ADR-0033
  // Phase C); "team:<id>" ⇒ a team (Phase F). agent_id/team_id are mutually exclusive,
  // so ONE combined picker encodes both (the value carries the type).
  // "" is now UNSET, NOT "the system agent" — `app_assistant` is retired (ADR-0071), so
  // there is no fallback and the backend REFUSES an unbound chat/voice interface
  // (422 `apps_interface:agent_required`). The Save button gates on it below, so the
  // operator is stopped here rather than by a 422.
  const [assistant, setAssistant] = React.useState(
    iface?.team_id ? `team:${iface.team_id}` : iface?.agent_id ? `agent:${iface.agent_id}` : "",
  );

  const configsQ = useQuery({
    queryKey: ["ai", "llm-configs"],
    queryFn: () => llmConfigsApi.list(),
    retry: false, // an operator without ai:read just sees the inherit option
  });
  const configs = configsQ.data?.configs ?? [];
  const agentsQ = useQuery({
    queryKey: ["ai", "agents"],
    queryFn: () => agentsApi.list(),
    retry: false, // without ai:read the picker is empty — see the no-agents hint
  });
  const customAgents = (agentsQ.data?.agents ?? []).filter((a) => !a.is_system);
  const teamsQ = useQuery({
    queryKey: ["ai", "teams"],
    queryFn: () => teamsApi.list(),
    retry: false, // without teams:read the picker just omits the teams group
  });
  const teams = teamsQ.data?.teams ?? [];

  const save = useMutation({
    mutationFn: () => {
      const cleanStarters = starters.map((s) => s.trim()).filter(Boolean);
      const config = {
        persona: persona.trim() || undefined,
        // Wake word is a voice-only edge; never persisted for chat.
        wake_word: isVoice && wakeWord ? true : undefined,
        // Empty-chat suggestions (undefined ⇒ the role-grounded fallback on the portal).
        starters: cleanStarters.length ? cleanStarters : undefined,
      };
      const llm_config_id = llmConfigId || null;
      const agent_id = assistant.startsWith("agent:") ? assistant.slice(6) : null;
      const team_id = assistant.startsWith("team:") ? assistant.slice(5) : null;
      if (iface) {
        return appsApi.updateInterface(appId, iface.id, {
          name: name.trim(),
          config,
          enabled,
          standalone,
          embedded,
          llm_config_id,
          agent_id,
          team_id,
        });
      }
      // Panel only: turning it on IS creating it. Left off ⇒ nothing to persist.
      if (gateCreateOnEnabled && !enabled) return Promise.resolve(null);
      return appsApi.createInterface(appId, {
        kind, name: name.trim(), config, standalone, embedded, llm_config_id, agent_id, team_id,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apps", appId, "interfaces"] });
      onSaved?.();
    },
  });

  // AI bootstrap: draft starter questions from the app schema (nothing saved server-side —
  // they're merged into the editable list for the operator to keep/trim). 422 = no LLM.
  const propose = async () => {
    setProposeErr(null);
    setProposing(true);
    try {
      const res = await appsApi.proposeAssistantStarters(appId);
      setStarters((cur) => {
        const have = new Set(cur.map((s) => s.trim()));
        return [...cur, ...res.starters.filter((s) => !have.has(s.trim()))];
      });
    } catch {
      setProposeErr(t("apps.startersProposeErr", "Proposta non riuscita (LLM non configurato?)."));
    } finally {
      setProposing(false);
    }
  };

  const fields = (
    <>
      <div className="space-y-1.5">
        <Label htmlFor="vi-name">{t("apps.interfaceName", "Name")}</Label>
        <Input id="vi-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="vi-persona">{t("apps.interfacePersona", "Persona")}</Label>
        <textarea
          id="vi-persona"
          value={persona}
          rows={3}
          placeholder={t(
            "apps.interfacePersonaHint",
            "Optional — how the assistant should sound (tone, style).",
          )}
          onChange={(e) => setPersona(e.target.value)}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
        />
      </div>
      {/* Empty-chat suggestions (Wave-1): operator-authored questions, drafted via AI.
          They WIN over the role-grounded fallback on the portal. */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label>{t("apps.interfaceStarters", "Domande suggerite")}</Label>
          <button
            type="button"
            onClick={() => void propose()}
            disabled={proposing}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-foreground transition hover:bg-elevated disabled:opacity-50"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {proposing
              ? t("apps.startersProposing", "Proposta…")
              : t("apps.startersPropose", "Proponi con AI")}
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          {t(
            "apps.startersHint",
            "Domande mostrate nella chat vuota. Lascia vuoto per suggerimenti automatici basati sui dati leggibili dal ruolo.",
          )}
        </p>
        {proposeErr && <p className="text-xs text-danger">{proposeErr}</p>}
        <div className="space-y-1.5">
          {starters.map((s, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <Input
                value={s}
                placeholder={t("apps.starterPlaceholder", "Es. Mostrami le vendite del mese")}
                onChange={(e) =>
                  setStarters((cur) => cur.map((v, j) => (j === i ? e.target.value : v)))
                }
              />
              <button
                type="button"
                aria-label={t("apps.starterRemove", "Rimuovi")}
                onClick={() => setStarters((cur) => cur.filter((_, j) => j !== i))}
                className="shrink-0 rounded p-1.5 text-muted-foreground transition hover:text-danger"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setStarters((cur) => [...cur, ""])}
            className="inline-flex items-center gap-1 text-xs font-medium text-accent transition hover:underline"
          >
            <Plus className="h-3.5 w-3.5" /> {t("apps.starterAdd", "Aggiungi domanda")}
          </button>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="vi-agent">{t("apps.interfaceAgent", "Assistente")}</Label>
        {/* Custom dropdown, never a native <select> (frontend/CLAUDE.md standing rule):
            the OS paints the option list, so `optgroup` can't be themed — the
            Agenti/Squadre split rides each option's `description` instead. */}
        <Combobox
          id="vi-agent"
          value={assistant}
          onChange={(v) => setAssistant(v ?? "")}
          placeholder={t("apps.interfaceAgentPick", "Scegli un agente…")}
          options={[
            ...customAgents.map((a) => ({
              value: `agent:${a.id}`,
              label: a.name,
              description: t("apps.interfaceAgentGroup", "Agente"),
            })),
            ...teams.map((tm) => ({
              value: `team:${tm.id}`,
              label: tm.name,
              description: t("apps.interfaceTeamGroup", "Squadra"),
            })),
          ]}
        />
        <p className="text-xs text-muted-foreground">
          {t(
            "apps.interfaceAgentHint",
            "Obbligatorio: un'interfaccia chat/voce deve indicare l'agente (o la squadra) che la guida. Usa le sue istruzioni e i suoi strumenti; l'ambito dati resta quello del ruolo.",
          )}{" "}
          <Link to="/ai/agents" className="text-accent underline" target="_blank" rel="noreferrer">
            {customAgents.length === 0 || teams.length === 0
              ? t("apps.interfaceAgentCreate", "Crea un agente")
              : t("apps.interfaceAgentManage", "Gestisci gli agenti")}
          </Link>
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="vi-llm">{t("apps.interfaceModel", "Modello (configurazione LLM)")}</Label>
        <Combobox
          id="vi-llm"
          value={llmConfigId}
          onChange={(v) => setLlmConfigId(v ?? "")}
          options={[
            { value: "", label: t("apps.interfaceModelInherit", "Eredita (agente predefinito)") },
            ...configs.map((c) => ({ value: c.id, label: c.name })),
          ]}
        />
        <p className="text-xs text-muted-foreground">
          {t(
            "apps.interfaceModelHint",
            "Sovrascrive il modello dell'agente voice/chat globale solo per questa app.",
          )}
        </p>
      </div>
      <p className="rounded-md border border-border bg-elevated/40 px-3 py-2 text-xs text-muted-foreground">
        {t(
          "apps.interfaceScopeHint",
          "What this assistant can read is set per role, in Members & roles → Data access — not here. It inherits each role's readable rows & columns by default.",
        )}
      </p>
      {isVoice && (
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label htmlFor="vi-wake">{t("apps.interfaceWakeWord", "Always listening")}</Label>
            <p className="text-xs text-muted-foreground">
              {t(
                "apps.interfaceWakeWordHint",
                'Hands-free: say "hey jarvis" to open. Runs fully in the browser; needs a secure (HTTPS) page.',
              )}
            </p>
          </div>
          <Switch id="vi-wake" checked={wakeWord} onCheckedChange={setWakeWord} />
        </div>
      )}
      {/* ADR-0049 — how this interface is EXPOSED. The two flags are independent:
          a face can be a standalone surface AND embeddable as a widget. */}
      <div className="space-y-3 rounded-md border border-border p-3">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("apps.interfaceExposure", "Esposizione")}
        </p>
        <div className="flex items-start justify-between gap-3">
          <div>
            <Label htmlFor="vi-standalone">
              {t("apps.interfaceStandalone", "Superficie autonoma")}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t(
                "apps.interfaceStandaloneHint",
                "Ha un proprio URL/schermata a sé — raggiungibile da sola, anche senza web app.",
              )}
            </p>
          </div>
          <Switch id="vi-standalone" checked={standalone} onCheckedChange={setStandalone} />
        </div>
        <div className="flex items-start justify-between gap-3">
          <div>
            <Label htmlFor="vi-embedded">
              {t("apps.interfaceEmbedded", "Widget incorporabile")}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t(
                "apps.interfaceEmbeddedHint",
                "Può essere montata come widget dentro la web app (assistente flottante).",
              )}
            </p>
          </div>
          <Switch id="vi-embedded" checked={embedded} onCheckedChange={setEmbedded} />
        </div>
      </div>
    </>
  );

  // ADR-0071: a chat/voice interface MUST name an agent (or a team) — there is no
  // `app_assistant` fallback, and the backend refuses an unbound one (422). Gating Save
  // here (with the state) rather than in each wrapper keeps ONE rule.
  const canSave = Boolean(name.trim()) && Boolean(assistant);
  return { fields, save, enabled, setEnabled, name, isVoice, canSave };
}

/**
 * Inline builder panel (owner UX, 2026-07-03): the Chat/Voce tab shows ALL the
 * interface settings up front with an **Abilitato** toggle that governs its
 * presence — no "create" step, no list.
 */
function AssistantInterfacePanel({
  appId,
  kind,
  iface,
  canWrite,
  loading,
  onSaved,
}: {
  appId: string;
  kind: AssistantKind;
  iface: InterfaceOut | null;
  canWrite: boolean;
  loading: boolean;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const { fields, save, enabled, setEnabled, isVoice, canSave } = useAssistantInterfaceForm({
    appId,
    kind,
    iface,
    onSaved,
    gateCreateOnEnabled: true,
  });

  if (loading) {
    return <div className="p-2 text-sm text-muted-foreground">…</div>;
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <SectionHeader
        icon={isVoice ? Mic : MessageSquare}
        title={
          isVoice
            ? t("apps.interfaceVoice", "Voice assistant")
            : t("apps.interfaceChat", "Chat assistant")
        }
      />
      <p className="text-xs text-muted-foreground">
        {t(
          "apps.interfacesHelp",
          "A face over this app's data. The web portal is always on; add a voice or chat assistant that answers over the same tables (respecting each user's permissions). What it may read is set per role.",
        )}
      </p>

      {/* Abilitato — the single control that decides whether the interface exists. */}
      <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-surface/40 px-4 py-3">
        <div>
          <Label htmlFor="vi-enabled" className="text-sm font-medium">
            {t("apps.enabledLabel", "Enabled")}
          </Label>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t(
              "apps.interfaceEnabledHint",
              "Attiva questa interfaccia per renderla disponibile agli utenti. Disattivandola resta configurata ma non viene mostrata.",
            )}
          </p>
        </div>
        <Switch id="vi-enabled" checked={enabled} onCheckedChange={setEnabled} disabled={!canWrite} />
      </div>

      <div className="space-y-4">{fields}</div>

      {save.isError && (
        <Alert tone="danger" title={t("apps.interfaceSaveFailed", "Could not save")}>
          <span className="font-mono text-xs">{(save.error as Error).message}</span>
        </Alert>
      )}

      {canWrite && (
        <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
          {save.isSuccess && !save.isPending && (
            <span className="text-sm text-success">{t("common.saved", "Salvato")}</span>
          )}
          <Button disabled={!canSave || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? t("common.saving", "Salvataggio…") : t("common.save", "Save")}
          </Button>
        </div>
      )}
    </div>
  );
}

/** Legacy edit/create dialog — reached only from the full (`!focusKind`) list. */
function AssistantInterfaceDialog({
  appId,
  kind,
  iface,
  onClose,
}: {
  appId: string;
  kind: AssistantKind;
  iface: InterfaceOut | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { fields, save, enabled, setEnabled, isVoice, canSave } = useAssistantInterfaceForm({
    appId,
    kind,
    iface,
    onSaved: onClose,
  });

  const title = iface
    ? isVoice
      ? t("apps.interfaceEditVoice", "Voice assistant")
      : t("apps.interfaceEditChat", "Chat assistant")
    : isVoice
      ? t("apps.interfaceAddVoice", "Add voice assistant")
      : t("apps.interfaceAddChat", "Add chat assistant");

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {fields}
          {iface && (
            <div className="flex items-center justify-between">
              <Label htmlFor="vi-enabled">{t("apps.enabledLabel", "Enabled")}</Label>
              <Switch id="vi-enabled" checked={enabled} onCheckedChange={setEnabled} />
            </div>
          )}
          {save.isError && (
            <Alert tone="danger" title={t("apps.interfaceSaveFailed", "Could not save")}>
              <span className="font-mono text-xs">{(save.error as Error).message}</span>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={!canSave || save.isPending} onClick={() => save.mutate()}>
            {t("common.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
